import { Injectable, inject, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { ActivatedRouteSnapshot } from "@angular/router";
import { catchError, map, Observable, of, tap, throwError } from "rxjs";
import { ToastService } from "src/app/shared/services/toast.service";
import { TranslateService } from "@ngx-translate/core";
import { getOnboardingStage } from "src/app/core/guards/first-time-user-experience.helper";
import { ProfileService } from "src/app/pages/profile/profile.service";
import { UserProfile } from "src/app/pages/profile/profile.model";
import { StoreService } from "src/app/services/apps/store/store.service";
import {
  catchApiError,
  createSupportHistoryContext,
  recordApiRequestStarted,
  recordApiRequestSucceeded,
  SupportHistoryRecorder,
} from "src/app/shared/helpers/support-api-history.helper";
import { ViewContextHistoryStateService } from "src/app/shared/services/view-context-history-state.service";
import { environment } from "src/environments/environment";

/** The onboarding modal's variants. */
export type OnboardingMomentStep =
  | "welcomeFirstTimeOnboarding"
  | "resumeIncompleteStoreSetup"
  | "afterLinkingStore"
  | "afterCompleteDeliveryAndThresholdForm"
  | "delegatedWelcome";

/** `MAT_DIALOG_DATA` of the onboarding modal. */
export interface OnboardingModalData {
  momentStep: OnboardingMomentStep;
  /** Route the modal was opened from; lets the modal record support history. */
  parentRoute?: ActivatedRouteSnapshot;
}

export interface OnboardingPotentialCasesResponse {
  storeId: string;
  potentialCases: Array<{
    claimType: string;
    amountPotential: number;
    quantity: number;
  }>;
  totalQuantityPotential: number;
  totalAmountPotential: number;
}

@Injectable({
  providedIn: "root",
})
export class OnboardingModalService {
  // Session-only "already shown" gates: each modal reappears on a fresh reload
  // until onboarding is done.
  hasSeenWelcomeFirstTimeOnboarding = signal(false);
  hasSeenResumeIncompleteStoreSetup = signal(false);
  hasSeenAfterCompleteDeliveryAndThresholdForm = signal(false);

  private http = inject(HttpClient);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private profileService = inject(ProfileService);
  private storeService = inject(StoreService);
  private viewContextHistorySvc = inject(ViewContextHistoryStateService);
  private apiUrl = `${environment.baseApiUrl}`;

  /**
   * Short-lived result of the last `prefetchOnboardingPotentialCases()` call,
   * so CandidateClaimsFoundResolver can reuse it instead of doing its own GET
   * an instant later when the caller navigates there right after prefetching.
   * Consumed (and cleared) on first read; ignored once stale.
   */
  private lastEnsuredResult: {
    storeId: string;
    response: OnboardingPotentialCasesResponse;
    at: number;
  } | null = null;
  private static readonly ENSURED_RESULT_MAX_AGE_MS = 10_000;

  /**
   * Records an onboarding event in the Contact Us support history. A no-op
   * without a route, since support events are keyed by the route they came from.
   */
  recordSupportHistory(
    route: ActivatedRouteSnapshot | undefined,
    ...[eventAction, eventDescription, options]: Parameters<SupportHistoryRecorder>
  ): void {
    if (!route) {
      return;
    }
    const stores = this.storeService.stores() ?? [];
    createSupportHistoryContext(this.viewContextHistorySvc, {
      titleView: "Onboarding",
      eventAction,
      eventDescription,
      thisState: {
        onboardingStage: getOnboardingStage(
          this.profileService.userProfile(),
          stores,
        ),
        storeIds: stores.map((store) => store.storeId),
      },
      route,
      options,
    });
  }

  /**
   * The one place that finishes onboarding. Emits the updated profile, or null
   * when the request failed (the profile service already toasts the failure),
   * after recording either outcome for support.
   */
  completeOnboarding(
    route: ActivatedRouteSnapshot | undefined,
    reason: string,
  ): Observable<UserProfile | null> {
    const record: SupportHistoryRecorder = (...args) =>
      this.recordSupportHistory(route, ...args);
    const context = { reason };

    recordApiRequestStarted(record, "complete onboarding", context);
    return this.profileService.markOnboardingAsComplete().pipe(
      map((profile): UserProfile | null => profile),
      tap(() =>
        recordApiRequestSucceeded(record, "complete onboarding", "mutation", context),
      ),
      catchApiError<UserProfile | null>(record, "complete onboarding", {
        context,
        fallback: of(null),
      }),
    );
  }

  /**
   * Finishes onboarding only when the user has nothing left to set up: every
   * store is shared with them, so there is nothing to link, analyze or pay for.
   * Any other stage keeps its own step (an owned store still needs its claims
   * and billing). Emits null when onboarding was not finished.
   */
  completeOnboardingWhenDelegatedReady(
    route: ActivatedRouteSnapshot | undefined,
  ): Observable<UserProfile | null> {
    const stage = getOnboardingStage(
      this.profileService.userProfile(),
      this.storeService.stores() ?? [],
    );
    return stage === "delegated-ready"
      ? this.completeOnboarding(route, "all stores shared with the user")
      : of(null);
  }

  getOnboardingPotentialCases(
    storeId: string,
  ): Observable<OnboardingPotentialCasesResponse> {
    const url = `${this.apiUrl}/claims/onboarding-potential-cases`;
    return this.http
      .get<OnboardingPotentialCasesResponse>(url, {
        params: {
          storeId,
        },
      })
      .pipe(
        catchError((error) => {
          if (error.status === 404) {
            return of({
              storeId,
              potentialCases: [],
              totalQuantityPotential: 0,
              totalAmountPotential: 0,
            });
          }
          this.toast.error(
            this.translate.instant(
              "toast_errors.getOnboardingPotentialCasesFailed",
            ),
          );
          return throwError(() => error);
        }),
      );
  }

  /**
   * Best-effort prefetch, run right before navigating to candidate-claims-found
   * (after linking/configuring a store) so `CandidateClaimsFoundResolver`
   * doesn't have to make its own GET an instant later. The backend now starts
   * the potential-cases job itself as soon as a store gets linked, so this no
   * longer creates or "ensures" any job — it's just a GET.
   */
  prefetchOnboardingPotentialCases(
    storeId: string,
  ): Observable<OnboardingPotentialCasesResponse> {
    return this.getOnboardingPotentialCases(storeId).pipe(
      tap((response) => {
        this.lastEnsuredResult = { storeId, response, at: Date.now() };
      }),
    );
  }

  /**
   * Reuses the response from a just-completed `prefetchOnboardingPotentialCases()`
   * call for the same store, so CandidateClaimsFoundResolver doesn't make an
   * immediate duplicate GET. Consumed on first read (cleared) and ignored
   * once older than a few seconds, so unrelated/late navigations (WS
   * re-navigation, direct URL visits, page reload) still get a fresh GET.
   */
  consumeEnsuredResult(
    storeId: string,
  ): OnboardingPotentialCasesResponse | null {
    const cached = this.lastEnsuredResult;
    this.lastEnsuredResult = null;
    if (!cached || cached.storeId !== storeId) {
      return null;
    }
    if (Date.now() - cached.at > OnboardingModalService.ENSURED_RESULT_MAX_AGE_MS) {
      return null;
    }
    return cached.response;
  }
}
